import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useAuth } from "@/App";
import {
  Plug,
  Plus,
  Loader2,
  TestTube,
  Check,
  X,
  IndianRupee,
  CreditCard,
  Shield,
  MessageSquare,
  QrCode,
  Building2,
  Wifi,
  Play,
  AlertCircle,
  CheckCircle2,
  Copy,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Wallet,
  TrendingDown,
  Users,
} from "lucide-react";

// ==================== INTEGRATIONS PAGE ====================
interface Integration {
  id: string;
  name: string;
  provider: string;
  type: string;
  environment: string;
  isActive: boolean;
  lastTestedAt: string | null;
  lastTestResult: string | null;
  createdAt: string;
}

const PROVIDERS: Record<string, string[]> = {
  CARD_ISSUER: ["AIRWALLEX", "NIUM", "M2P", "YAP", "ZETA", "MARQETA", "ISERVEU"],
  BANKING_PARTNER: ["ISERVEU"],
  PAYMENT_GATEWAY: ["PAYHUB"],
  KYC_PROVIDER: ["SIGNZY", "FRANKIE", "GREENID", "ONFIDO", "JUMIO", "AADHAAR_UIDAI"],
  SMS_PROVIDER: ["TWILIO", "MSG91"],
};

export default function IntegrationsPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER";
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    provider: "",
    type: "CARD_ISSUER",
    environment: "sandbox",
    apiKeyEncrypted: "",
    apiSecretEncrypted: "",
  });

  const { data: integrations, isLoading } = useQuery<Integration[]>({
    queryKey: ["integrations"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/integrations");
      if (!response.ok) throw new Error("Failed to fetch integrations");
      return response.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await apiRequest("POST", "/api/integrations", data);
      if (!response.ok) throw new Error("Failed to create integration");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      toast({ title: "Success", description: "Integration added" });
      setIsOpen(false);
      setFormData({ name: "", provider: "", type: "CARD_ISSUER", environment: "sandbox", apiKeyEncrypted: "", apiSecretEncrypted: "" });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const response = await apiRequest("PATCH", `/api/integrations/${id}`, { isActive });
      if (!response.ok) throw new Error("Failed to update integration");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
    },
  });

  const testMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("POST", `/api/integrations/${id}/test`);
      if (!response.ok) throw new Error("Test failed");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      toast({ title: "Success", description: "Connection test passed" });
    },
    onError: () => {
      toast({ title: "Error", description: "Connection test failed", variant: "destructive" });
    },
  });

  const getTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      CARD_ISSUER: "bg-blue-500/10 text-blue-500",
      KYC_PROVIDER: "bg-green-500/10 text-green-500",
      SMS_PROVIDER: "bg-purple-500/10 text-purple-500",
      BANKING_PARTNER: "bg-amber-500/10 text-amber-500",
      PAYMENT_GATEWAY: "bg-emerald-500/10 text-emerald-500",
    };
    const labels: Record<string, string> = {
      CARD_ISSUER: "Card Issuer",
      KYC_PROVIDER: "KYC Provider",
      SMS_PROVIDER: "SMS Provider",
      BANKING_PARTNER: "Banking Partner",
      PAYMENT_GATEWAY: "Payment Gateway",
    };
    return <Badge className={colors[type] || ""}>{labels[type] || type}</Badge>;
  };

  const getProviderIcon = (provider: string) => {
    if (["ISERVEU", "M2P", "YAP", "ZETA"].includes(provider)) return <IndianRupee className="h-4 w-4" />;
    if (["PAYHUB"].includes(provider)) return <QrCode className="h-4 w-4" />;
    if (["SIGNZY"].includes(provider)) return <Shield className="h-4 w-4" />;
    if (["AADHAAR_UIDAI"].includes(provider)) return <Shield className="h-4 w-4" />;
    return <CreditCard className="h-4 w-4" />;
  };

  // PayHub Testing State
  const [expandedIntegrationId, setExpandedIntegrationId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<{
    connection?: any;
    payin?: any;
    payinStatus?: any;
    payinBalance?: any;
    payoutStatus?: any;
    payoutBalance?: any;
  }>({});

  // Signzy Testing State
  const [signzyTestResults, setSignzyTestResults] = useState<{
    connection?: any;
    createMerchant?: any;
    getMerchant?: any;
  }>({});

  // Fetch PayHub status
  const { data: payhubStatusData, refetch: refetchPayhubStatus } = useQuery({
    queryKey: ["payhub-status"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/payhub/status");
      if (!response.ok) throw new Error("Failed to fetch PayHub status");
      return response.json();
    },
  });

  // Test PayHub connection
  const testConnectionMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("GET", "/api/payhub/test");
      if (!response.ok) throw new Error("Connection test failed");
      return response.json();
    },
    onSuccess: (data) => {
      setTestResults((prev) => ({ ...prev, connection: data }));
      toast({ 
        title: data.connection.success ? "Connection Successful" : "Connection Failed", 
        description: data.connection.message,
        variant: data.connection.success ? "default" : "destructive",
      });
    },
    onError: (error) => {
      toast({ title: "Test Failed", description: String(error), variant: "destructive" });
    },
  });

  // Test Payin Intent (UPI deep links)
  const testPayinIntentMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/india/payments/payin-intent", {
        customer_email: "test@vedalogic.io",
        phone: "+919876543210",
        username: "Test Customer",
        amount: 100, // Rs 100.00 (minimum required by PayHub)
        customerId: "test_customer",
      });
      if (!response.ok) throw new Error("Payin intent creation failed");
      return response.json();
    },
    onSuccess: (data) => {
      setTestResults((prev) => ({ ...prev, payin: data }));
      toast({ 
        title: "Payin Intent Created", 
        description: "UPI payment links generated successfully",
      });
    },
    onError: (error) => {
      toast({ title: "Payin Intent Failed", description: String(error), variant: "destructive" });
    },
  });

  // Test Payin Status
  const testPayinStatusMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/payhub/payin-status", {
        transaction_id: "test_transaction_123",
        date: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
      });
      if (!response.ok) throw new Error("Payin status check failed");
      return response.json();
    },
    onSuccess: (data) => {
      setTestResults((prev) => ({ ...prev, payinStatus: data }));
      toast({ 
        title: "Payin Status Retrieved", 
        description: `Status: ${data.responseData?.status || 'N/A'}`,
      });
    },
    onError: (error) => {
      toast({ title: "Payin Status Failed", description: String(error), variant: "destructive" });
    },
  });

  // Test Payin Balance
  const testPayinBalanceMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("GET", "/api/payhub/payin-balance");
      if (!response.ok) throw new Error("Payin balance check failed");
      return response.json();
    },
    onSuccess: (data) => {
      setTestResults((prev) => ({ ...prev, payinBalance: data }));
      toast({ 
        title: "Payin Balance Retrieved", 
        description: `Balance: Rs ${data.responseData?.balance || 0}`,
      });
    },
    onError: (error) => {
      toast({ title: "Payin Balance Failed", description: String(error), variant: "destructive" });
    },
  });

  // Test Payout Status
  const testPayoutStatusMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/payhub/payout-status", {
        transaction_id: "test_payout_123",
      });
      if (!response.ok) throw new Error("Payout status check failed");
      return response.json();
    },
    onSuccess: (data) => {
      setTestResults((prev) => ({ ...prev, payoutStatus: data }));
      toast({ 
        title: "Payout Status Retrieved", 
        description: `Status: ${data.responseData?.status || 'N/A'}`,
      });
    },
    onError: (error) => {
      toast({ title: "Payout Status Failed", description: String(error), variant: "destructive" });
    },
  });

  // Test Payout Balance
  const testPayoutBalanceMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("GET", "/api/payhub/payout-balance");
      if (!response.ok) throw new Error("Payout balance check failed");
      return response.json();
    },
    onSuccess: (data) => {
      setTestResults((prev) => ({ ...prev, payoutBalance: data }));
      toast({ 
        title: "Payout Balance Retrieved", 
        description: `Balance: Rs ${data.responseData?.balance || 0}`,
      });
    },
    onError: (error) => {
      toast({ title: "Payout Balance Failed", description: String(error), variant: "destructive" });
    },
  });

  // ==================== SIGNZY MUTATIONS ====================
  
  // Test Signzy connection
  const testSignzyConnectionMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("GET", "/api/signzy/test");
      if (!response.ok) throw new Error("Connection test failed");
      return response.json();
    },
    onSuccess: (data) => {
      setSignzyTestResults((prev) => ({ ...prev, connection: data }));
      toast({ 
        title: data.connection.success ? "Connection Successful" : "Connection Failed", 
        description: data.connection.message,
        variant: data.connection.success ? "default" : "destructive",
      });
    },
    onError: (error) => {
      toast({ title: "Test Failed", description: String(error), variant: "destructive" });
    },
  });

  // Create Signzy merchant (test KYC journey)
  const createSignzyMerchantMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/signzy/create-merchant", {
        name: "Test Customer",
        email: "test@vedalogic.io",
        mobile: "+919876543210",
      });
      if (!response.ok) throw new Error("Failed to create merchant");
      return response.json();
    },
    onSuccess: (data) => {
      setSignzyTestResults((prev) => ({ ...prev, createMerchant: data }));
      toast({ 
        title: "KYC Journey Created", 
        description: `Merchant ID: ${data.merchantId}`,
      });
    },
    onError: (error) => {
      toast({ title: "Create Failed", description: String(error), variant: "destructive" });
    },
  });

  // Get Signzy merchant status
  const getSignzyMerchantMutation = useMutation({
    mutationFn: async (merchantId: string) => {
      const response = await apiRequest("GET", `/api/signzy/merchant/${merchantId}`);
      if (!response.ok) throw new Error("Failed to fetch merchant");
      return response.json();
    },
    onSuccess: (data) => {
      setSignzyTestResults((prev) => ({ ...prev, getMerchant: data }));
      toast({ 
        title: "Merchant Data Retrieved", 
        description: `Status: ${data.status}`,
      });
    },
    onError: (error) => {
      toast({ title: "Fetch Failed", description: String(error), variant: "destructive" });
    },
  });

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied", description: "Copied to clipboard" });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Integration Partners</h2>
          <p className="text-muted-foreground mt-2">Configure regional providers for card issuance, KYC, and payments</p>
        </div>
        {isSuperAdmin && (
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button><Plus className="mr-2 h-4 w-4" />Add Integration</Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Integration</DialogTitle>
            </DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(formData); }} className="space-y-4">
              <div className="space-y-2">
                <Label>Integration Type</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v, provider: "" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BANKING_PARTNER">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-amber-500" />
                        <span>Banking Partner</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="PAYMENT_GATEWAY">
                      <div className="flex items-center gap-2">
                        <QrCode className="h-4 w-4 text-emerald-500" />
                        <span>Payment Gateway</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="CARD_ISSUER">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-blue-500" />
                        <span>Card Issuer</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="KYC_PROVIDER">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-green-500" />
                        <span>KYC Provider</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="SMS_PROVIDER">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-purple-500" />
                        <span>SMS Provider</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Provider</Label>
                <Select value={formData.provider} onValueChange={(v) => setFormData({ ...formData, provider: v, name: v })}>
                  <SelectTrigger><SelectValue placeholder="Select provider" /></SelectTrigger>
                  <SelectContent>
                    {PROVIDERS[formData.type]?.map((p) => (
                      <SelectItem key={p} value={p}>
                        <div className="flex items-center gap-2">
                          {getProviderIcon(p)}
                          <span>{p}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Display Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Environment</Label>
                <Select value={formData.environment} onValueChange={(v) => setFormData({ ...formData, environment: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Sandbox</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>API Key</Label>
                <Input type="password" value={formData.apiKeyEncrypted} onChange={(e) => setFormData({ ...formData, apiKeyEncrypted: e.target.value })} placeholder="sk_test_..." />
              </div>
              <Button type="submit" disabled={createMutation.isPending || !formData.provider}>
                {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Add Integration
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* India Partners Card */}
      <Card className="border-l-4 border-l-amber-500">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <IndianRupee className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <CardTitle className="text-base">India Market Partners</CardTitle>
              <CardDescription>Core integration partners for India operations</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <Card className="hover:border-primary/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/30">
                    <Building2 className="h-4 w-4 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">Host Server</h4>
                    <p className="text-xs text-muted-foreground mt-1">RuPay card issuance</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="hover:border-primary/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/30">
                    <IndianRupee className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">PayHub</h4>
                    <p className="text-xs text-muted-foreground mt-1">UPI payments & payouts</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="hover:border-primary/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/30">
                    <Shield className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">Signzy</h4>
                    <p className="text-xs text-muted-foreground mt-1">Video KYC verification</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="hover:border-primary/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30">
                    <Shield className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">Aadhaar UIDAI</h4>
                    <p className="text-xs text-muted-foreground mt-1">National ID verification</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Configured Integrations Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted">
              <Plug className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <CardTitle className="text-base">Configured Integrations</CardTitle>
              <CardDescription>{integrations?.length ?? 0} integration(s) configured</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : integrations?.length === 0 ? (
            <div className="text-center py-12 border-t">
              <Plug className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No integrations configured</h3>
              <p className="text-muted-foreground">Add your first integration above.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Environment</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {integrations?.map((int) => (
                  <>
                    <TableRow key={int.id} className="group">
                      <TableCell className="font-medium">{int.name}</TableCell>
                      <TableCell>{getTypeBadge(int.type)}</TableCell>
                      <TableCell><Badge variant="outline">{int.provider}</Badge></TableCell>
                      <TableCell><Badge variant={int.environment === "production" ? "default" : "secondary"}>{int.environment}</Badge></TableCell>
                      <TableCell>
                        {int.lastTestResult === "SUCCESS" ? (
                          <Badge variant="success"><Check className="mr-1 h-3 w-3" />Passed</Badge>
                        ) : int.lastTestResult === "FAILED" ? (
                          <Badge variant="destructive"><X className="mr-1 h-3 w-3" />Failed</Badge>
                        ) : (
                          <Badge variant="secondary">Not tested</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={int.isActive}
                          onCheckedChange={(checked) => toggleMutation.mutate({ id: int.id, isActive: checked })}
                          disabled={!isSuperAdmin}
                        />
                      </TableCell>
                      <TableCell>
                        {isSuperAdmin && (
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" onClick={() => testMutation.mutate(int.id)}>
                              <TestTube className="h-4 w-4" />
                            </Button>
                            {(int.provider === "PAYHUB" || int.provider === "SIGNZY") && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setExpandedIntegrationId(expandedIntegrationId === int.id ? null : int.id)}
                              >
                                {expandedIntegrationId === int.id ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                    
                    {/* PayHub Testing Panel */}
                    {isSuperAdmin && int.provider === "PAYHUB" && expandedIntegrationId === int.id && (
                      <TableRow>
                        <TableCell colSpan={7} className="bg-muted/30 p-0">
                          <div className="p-6 space-y-4">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-2">
                                <IndianRupee className="h-5 w-5 text-emerald-600" />
                                <h4 className="font-semibold">PayHub Testing Panel</h4>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => refetchPayhubStatus()}
                              >
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Refresh Status
                              </Button>
                            </div>

                            {/* Connection Status */}
                            <div className="p-4 rounded-lg border bg-background">
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-2">
                                  {payhubStatusData?.configured ? (
                                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                                  ) : (
                                    <AlertCircle className="h-5 w-5 text-amber-500" />
                                  )}
                                  <h5 className="font-semibold text-sm">Connection Status</h5>
                                </div>
                                <Badge variant={payhubStatusData?.configured ? "success" : "secondary"}>
                                  {payhubStatusData?.configured ? "Configured" : "Not Configured"}
                                </Badge>
                              </div>
                              
                              <div className="grid gap-3 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Environment:</span>
                                  <Badge variant="outline">{payhubStatusData?.environment || "Unknown"}</Badge>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Base URL:</span>
                                  <code className="text-xs bg-muted px-2 py-1 rounded">{payhubStatusData?.baseUrl || "Not set"}</code>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Webhook Configured:</span>
                                  <Badge variant={payhubStatusData?.webhookConfigured ? "success" : "secondary"}>
                                    {payhubStatusData?.webhookConfigured ? "Yes" : "No"}
                                  </Badge>
                                </div>
                                {testResults.connection && (
                                  <>
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Last Test:</span>
                                      <Badge variant={testResults.connection.connection.success ? "success" : "destructive"}>
                                        {testResults.connection.connection.success ? "Passed" : "Failed"}
                                      </Badge>
                                    </div>
                                    {testResults.connection.connection.latency && (
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Latency:</span>
                                        <span className="font-mono text-xs">{testResults.connection.connection.latency}ms</span>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Test Actions */}
                            <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-5">
                              {/* Connection Test */}
                              <div className="p-4 rounded-lg border bg-background hover:border-primary/30 transition-colors">
                                <div className="flex flex-col gap-3">
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30">
                                      <Wifi className="h-4 w-4 text-blue-600" />
                                    </div>
                                    <div className="flex-1">
                                      <h5 className="font-semibold text-sm">Connection Test</h5>
                                      <p className="text-xs text-muted-foreground mt-1">Verify API authentication</p>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => testConnectionMutation.mutate()}
                                    disabled={testConnectionMutation.isPending}
                                  >
                                    {testConnectionMutation.isPending ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Play className="mr-2 h-4 w-4" />
                                    )}
                                    Test Connection
                                  </Button>
                                </div>
                              </div>

                              {/* Payin Intent Test */}
                              <div className="p-4 rounded-lg border bg-background hover:border-primary/30 transition-colors">
                                <div className="flex flex-col gap-3">
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/30">
                                      <IndianRupee className="h-4 w-4 text-emerald-600" />
                                    </div>
                                    <div className="flex-1">
                                      <h5 className="font-semibold text-sm">Payin Intent</h5>
                                      <p className="text-xs text-muted-foreground mt-1">UPI deep links (Rs 100.00)</p>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => testPayinIntentMutation.mutate()}
                                    disabled={testPayinIntentMutation.isPending}
                                  >
                                    {testPayinIntentMutation.isPending ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Play className="mr-2 h-4 w-4" />
                                    )}
                                    Test Payin
                                  </Button>
                                </div>
                              </div>

                              {/* Payin Balance Test */}
                              <div className="p-4 rounded-lg border bg-background hover:border-primary/30 transition-colors">
                                <div className="flex flex-col gap-3">
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/30">
                                      <Wallet className="h-4 w-4 text-green-600" />
                                    </div>
                                    <div className="flex-1">
                                      <h5 className="font-semibold text-sm">Payin Balance</h5>
                                      <p className="text-xs text-muted-foreground mt-1">Check balance</p>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => testPayinBalanceMutation.mutate()}
                                    disabled={testPayinBalanceMutation.isPending}
                                  >
                                    {testPayinBalanceMutation.isPending ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Play className="mr-2 h-4 w-4" />
                                    )}
                                    Check
                                  </Button>
                                </div>
                              </div>

                              {/* Payout Status Test */}
                              <div className="p-4 rounded-lg border bg-background hover:border-primary/30 transition-colors">
                                <div className="flex flex-col gap-3">
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-orange-50 dark:bg-orange-900/30">
                                      <TrendingDown className="h-4 w-4 text-orange-600" />
                                    </div>
                                    <div className="flex-1">
                                      <h5 className="font-semibold text-sm">Payout Status</h5>
                                      <p className="text-xs text-muted-foreground mt-1">Check status</p>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => testPayoutStatusMutation.mutate()}
                                    disabled={testPayoutStatusMutation.isPending}
                                  >
                                    {testPayoutStatusMutation.isPending ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Play className="mr-2 h-4 w-4" />
                                    )}
                                    Test
                                  </Button>
                                </div>
                              </div>

                              {/* Payout Balance Test */}
                              <div className="p-4 rounded-lg border bg-background hover:border-primary/30 transition-colors">
                                <div className="flex flex-col gap-3">
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/30">
                                      <Wallet className="h-4 w-4 text-purple-600" />
                                    </div>
                                    <div className="flex-1">
                                      <h5 className="font-semibold text-sm">Payout Balance</h5>
                                      <p className="text-xs text-muted-foreground mt-1">Check balance</p>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => testPayoutBalanceMutation.mutate()}
                                    disabled={testPayoutBalanceMutation.isPending}
                                  >
                                    {testPayoutBalanceMutation.isPending ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Play className="mr-2 h-4 w-4" />
                                    )}
                                    Check
                                  </Button>
                                </div>
                              </div>
                            </div>

                            {/* Test Results Display */}
                            {(testResults.payin || testResults.payinStatus || testResults.payinBalance || testResults.payoutStatus || testResults.payoutBalance) && (
                              <div className="space-y-3">
                                <h5 className="font-semibold text-sm">Test Results</h5>
                                
                                {testResults.payin && (
                                  <div className="p-4 rounded-lg border bg-background space-y-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <IndianRupee className="h-4 w-4 text-emerald-600" />
                                        <span className="font-medium text-sm">Payin Request Created</span>
                                      </div>
                                      <Badge variant="success">Success</Badge>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Amount:</span>
                                        <span className="font-mono">Rs 100.00</span>
                                      </div>
                                      <div className="pt-2 border-t space-y-1">
                                        <p className="text-xs text-muted-foreground mb-2">UPI Payment Links:</p>
                                        {testResults.payin.responseData?.gpay && (
                                          <a href={testResults.payin.responseData.gpay} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline block">
                                            📱 Google Pay →
                                          </a>
                                        )}
                                        {testResults.payin.responseData?.phonepe && (
                                          <a href={testResults.payin.responseData.phonepe} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline block">
                                            📱 PhonePe →
                                          </a>
                                        )}
                                        {testResults.payin.responseData?.paytm && (
                                          <a href={testResults.payin.responseData.paytm} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline block">
                                            📱 Paytm →
                                          </a>
                                        )}
                                        {testResults.payin.responseData?.url && (
                                          <a href={testResults.payin.responseData.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline block">
                                            🔗 Generic UPI →
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {testResults.payinStatus && (
                                  <div className="p-4 rounded-lg border bg-background space-y-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <CheckCircle2 className="h-4 w-4 text-blue-600" />
                                        <span className="font-medium text-sm">Payin Status</span>
                                      </div>
                                      <Badge variant={testResults.payinStatus.responseCode === 200 ? "success" : "destructive"}>
                                        {testResults.payinStatus.responseCode === 200 ? "Success" : "Failed"}
                                      </Badge>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                      {testResults.payinStatus.responseData && (
                                        <>
                                          <div className="flex justify-between">
                                            <span className="text-muted-foreground">Status:</span>
                                            <Badge variant={testResults.payinStatus.responseData.status === 'success' ? "success" : "secondary"}>
                                              {testResults.payinStatus.responseData.status}
                                            </Badge>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-muted-foreground">Amount:</span>
                                            <span className="font-mono">Rs {testResults.payinStatus.responseData.amount}</span>
                                          </div>
                                          {testResults.payinStatus.responseData.utr && (
                                            <div className="flex justify-between">
                                              <span className="text-muted-foreground">UTR:</span>
                                              <code className="text-xs bg-muted px-2 py-1 rounded">{testResults.payinStatus.responseData.utr}</code>
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {testResults.payinBalance && (
                                  <div className="p-4 rounded-lg border bg-background space-y-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <Wallet className="h-4 w-4 text-green-600" />
                                        <span className="font-medium text-sm">Payin Balance</span>
                                      </div>
                                      <Badge variant={testResults.payinBalance.responseCode === 200 ? "success" : "destructive"}>
                                        {testResults.payinBalance.responseCode === 200 ? "Success" : "Failed"}
                                      </Badge>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                      {testResults.payinBalance.responseData && (
                                        <div className="flex justify-between items-center">
                                          <span className="text-muted-foreground">Available Balance:</span>
                                          <span className="font-mono text-lg font-semibold text-green-600">Rs {testResults.payinBalance.responseData.balance}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {testResults.payoutStatus && (
                                  <div className="p-4 rounded-lg border bg-background space-y-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <TrendingDown className="h-4 w-4 text-orange-600" />
                                        <span className="font-medium text-sm">Payout Status</span>
                                      </div>
                                      <Badge variant={testResults.payoutStatus.responseCode === 200 ? "success" : "destructive"}>
                                        {testResults.payoutStatus.responseCode === 200 ? "Success" : "Failed"}
                                      </Badge>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                      {testResults.payoutStatus.responseData && (
                                        <>
                                          <div className="flex justify-between">
                                            <span className="text-muted-foreground">Status:</span>
                                            <Badge variant={testResults.payoutStatus.responseData.status === 'success' ? "success" : "secondary"}>
                                              {testResults.payoutStatus.responseData.status}
                                            </Badge>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-muted-foreground">Amount:</span>
                                            <span className="font-mono">Rs {testResults.payoutStatus.responseData.amount}</span>
                                          </div>
                                          {testResults.payoutStatus.responseData.utr && (
                                            <div className="flex justify-between">
                                              <span className="text-muted-foreground">UTR:</span>
                                              <code className="text-xs bg-muted px-2 py-1 rounded">{testResults.payoutStatus.responseData.utr}</code>
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {testResults.payoutBalance && (
                                  <div className="p-4 rounded-lg border bg-background space-y-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <Wallet className="h-4 w-4 text-purple-600" />
                                        <span className="font-medium text-sm">Payout Balance</span>
                                      </div>
                                      <Badge variant={testResults.payoutBalance.responseCode === 200 ? "success" : "destructive"}>
                                        {testResults.payoutBalance.responseCode === 200 ? "Success" : "Failed"}
                                      </Badge>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                      {testResults.payoutBalance.responseData && (
                                        <div className="flex justify-between items-center">
                                          <span className="text-muted-foreground">Available Balance:</span>
                                          <span className="font-mono text-lg font-semibold text-purple-600">Rs {testResults.payoutBalance.responseData.balance}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Webhook Information */}
                            <div className="p-4 rounded-lg border bg-muted/50">
                              <div className="flex items-start gap-3">
                                <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
                                <div className="space-y-2 text-sm flex-1">
                                  <p className="font-medium">Webhook Endpoint</p>
                                  <div className="flex items-center gap-2">
                                    <code className="text-xs bg-background px-2 py-1 rounded flex-1">
                                      https://vl-vc-middlelayer.vercel.app/api/psps/gsx/webhook
                                    </code>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 w-6 p-0"
                                      onClick={() => copyToClipboard("https://vl-vc-middlelayer.vercel.app/api/psps/gsx/webhook")}
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    Configure this URL in PayHub dashboard to receive payment notifications
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Signzy KYC Testing Panel */}
                    {isSuperAdmin && int.provider === "SIGNZY" && expandedIntegrationId === int.id && (
                      <TableRow>
                        <TableCell colSpan={7} className="bg-muted/30 p-0">
                          <div className="p-6 space-y-4">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-2">
                                <Shield className="h-5 w-5 text-primary" />
                                <h4 className="font-semibold">Signzy Video KYC Testing Panel</h4>
                              </div>
                            </div>

                            {/* Test Actions */}
                            <div className="grid gap-3 md:grid-cols-3">
                              {/* Connection Test */}
                              <div className="p-4 rounded-lg border bg-background hover:border-primary/30 transition-colors">
                                <div className="flex flex-col gap-3">
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30">
                                      <Wifi className="h-4 w-4 text-blue-600" />
                                    </div>
                                    <div className="flex-1">
                                      <h5 className="font-semibold text-sm">Connection Test</h5>
                                      <p className="text-xs text-muted-foreground mt-1">Verify API authentication</p>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => testSignzyConnectionMutation.mutate()}
                                    disabled={testSignzyConnectionMutation.isPending}
                                  >
                                    {testSignzyConnectionMutation.isPending ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Play className="mr-2 h-4 w-4" />
                                    )}
                                    Test Connection
                                  </Button>
                                </div>
                              </div>

                              {/* Create Merchant Test */}
                              <div className="p-4 rounded-lg border bg-background hover:border-primary/30 transition-colors">
                                <div className="flex flex-col gap-3">
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/30">
                                      <Users className="h-4 w-4 text-emerald-600" />
                                    </div>
                                    <div className="flex-1">
                                      <h5 className="font-semibold text-sm">Create Merchant</h5>
                                      <p className="text-xs text-muted-foreground mt-1">Start KYC journey</p>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => createSignzyMerchantMutation.mutate()}
                                    disabled={createSignzyMerchantMutation.isPending}
                                  >
                                    {createSignzyMerchantMutation.isPending ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Play className="mr-2 h-4 w-4" />
                                    )}
                                    Create
                                  </Button>
                                </div>
                              </div>

                              {/* Get Status Test */}
                              <div className="p-4 rounded-lg border bg-background hover:border-primary/30 transition-colors">
                                <div className="flex flex-col gap-3">
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/30">
                                      <CheckCircle2 className="h-4 w-4 text-purple-600" />
                                    </div>
                                    <div className="flex-1">
                                      <h5 className="font-semibold text-sm">Get Status</h5>
                                      <p className="text-xs text-muted-foreground mt-1">Check merchant</p>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => {
                                      if (signzyTestResults.createMerchant?.merchantId) {
                                        getSignzyMerchantMutation.mutate(signzyTestResults.createMerchant.merchantId);
                                      } else {
                                        toast({ title: "No Merchant", description: "Create a merchant first", variant: "destructive" });
                                      }
                                    }}
                                    disabled={getSignzyMerchantMutation.isPending}
                                  >
                                    {getSignzyMerchantMutation.isPending ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Play className="mr-2 h-4 w-4" />
                                    )}
                                    Check
                                  </Button>
                                </div>
                              </div>
                            </div>

                            {/* Test Results Display */}
                            {(signzyTestResults.connection || signzyTestResults.createMerchant || signzyTestResults.getMerchant) && (
                              <div className="space-y-3">
                                <h5 className="font-semibold text-sm">Test Results</h5>
                                
                                {signzyTestResults.connection && (
                                  <div className="p-4 rounded-lg border bg-background space-y-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                                        <span className="font-medium text-sm">Connection Test</span>
                                      </div>
                                      <Badge variant={signzyTestResults.connection.connection.success ? "success" : "destructive"}>
                                        {signzyTestResults.connection.connection.success ? "Connected" : "Failed"}
                                      </Badge>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Environment:</span>
                                        <Badge variant="outline">{signzyTestResults.connection.environment || "Unknown"}</Badge>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Message:</span>
                                        <span className="text-xs">{signzyTestResults.connection.connection.message}</span>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {signzyTestResults.createMerchant && (
                                  <div className="p-4 rounded-lg border bg-background space-y-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <Users className="h-4 w-4 text-emerald-600" />
                                        <span className="font-medium text-sm">Merchant Created</span>
                                      </div>
                                      <Badge variant="success">Success</Badge>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between items-center">
                                        <span className="text-muted-foreground">Merchant ID:</span>
                                        <div className="flex items-center gap-2">
                                          <code className="text-xs bg-muted px-2 py-1 rounded">{signzyTestResults.createMerchant.merchantId}</code>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 w-6 p-0"
                                            onClick={() => copyToClipboard(signzyTestResults.createMerchant.merchantId)}
                                          >
                                            <Copy className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Signzy App ID:</span>
                                        <span className="font-mono text-xs">{signzyTestResults.createMerchant.signzyAppId}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Status:</span>
                                        <Badge variant="secondary">{signzyTestResults.createMerchant.status}</Badge>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Flow:</span>
                                        <span className="text-xs">{signzyTestResults.createMerchant.flowName}</span>
                                      </div>
                                      {signzyTestResults.createMerchant.autoLoginUrl && (
                                        <div className="pt-2 border-t space-y-1">
                                          <p className="text-xs text-muted-foreground mb-2">Customer KYC URL:</p>
                                          <div className="flex items-center gap-2">
                                            <a 
                                              href={signzyTestResults.createMerchant.autoLoginUrl} 
                                              target="_blank" 
                                              rel="noopener noreferrer" 
                                              className="text-xs text-primary hover:underline flex-1 truncate"
                                            >
                                              {signzyTestResults.createMerchant.shortAutoLoginUrl}
                                            </a>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-6 w-6 p-0"
                                              onClick={() => copyToClipboard(signzyTestResults.createMerchant.autoLoginUrl)}
                                            >
                                              <Copy className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {signzyTestResults.getMerchant && (
                                  <div className="p-4 rounded-lg border bg-background space-y-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <CheckCircle2 className="h-4 w-4 text-purple-600" />
                                        <span className="font-medium text-sm">Merchant Status</span>
                                      </div>
                                      <Badge variant={signzyTestResults.getMerchant.status === 'accepted' ? "success" : "secondary"}>
                                        {signzyTestResults.getMerchant.status}
                                      </Badge>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Name:</span>
                                        <span className="text-xs">{signzyTestResults.getMerchant.name}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Email:</span>
                                        <span className="text-xs">{signzyTestResults.getMerchant.email}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Flow:</span>
                                        <span className="text-xs">{signzyTestResults.getMerchant.flowName}</span>
                                      </div>
                                      {signzyTestResults.getMerchant.videoKyc && signzyTestResults.getMerchant.videoKyc.length > 0 && (
                                        <div className="pt-2 border-t space-y-1">
                                          <p className="text-xs text-muted-foreground mb-2">Video KYC Available:</p>
                                          <a 
                                            href={signzyTestResults.getMerchant.videoKyc[0].videoUrl} 
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className="text-xs text-primary hover:underline block"
                                          >
                                            🎥 View Video Recording →
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Webhook Information */}
                            <div className="p-4 rounded-lg border bg-muted/50">
                              <div className="flex items-start gap-3">
                                <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
                                <div className="space-y-2 text-sm flex-1">
                                  <p className="font-medium">Webhook Endpoint</p>
                                  <div className="flex items-center gap-2">
                                    <code className="text-xs bg-background px-2 py-1 rounded flex-1">
                                      https://vl-vc-middlelayer.vercel.app/api/webhooks/signzy
                                    </code>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 w-6 p-0"
                                      onClick={() => copyToClipboard("https://vl-vc-middlelayer.vercel.app/api/webhooks/signzy")}
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    Configure this URL in Signzy dashboard to receive KYC status updates
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
